/**
 * Profile Settings Component
 * 
 * Handles user profile configuration including personal information,
 * contact details, and account settings.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Avatar,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { useAuth } from '../../context/AuthContext';
import useCustomFetch from '../../hooks/useCustomFetch';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

function ProfileSettings() {
  const { user, updateUser } = useAuth();
  const { get, post, put } = useCustomFetch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [churchName, setChurchName] = useState('');

  const getFullAvatarUrl = (url) => {
    console.log('🖼️ [ProfileSettings] Getting full avatar URL for:', url);
    if (!url) {
      console.log('🖼️ [ProfileSettings] No URL provided, returning empty string');
      return '';
    }
    if (url.startsWith('http')) {
      console.log('🖼️ [ProfileSettings] URL is already absolute:', url);
      return url;
    }
    // Remove /api/v1 if it exists in the base URL since the upload path already includes it
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace('/api/v1', '');
    const fullUrl = `${baseUrl}${url}`;
    console.log('🖼️ [ProfileSettings] Converted to full URL:', fullUrl);
    return fullUrl;
  };

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    dateOfBirth: user?.dateOfBirth || null,
    instruments: user?.instruments || [],
    preferredLanguage: user?.preferredLanguage || 'en',
    avatar: getFullAvatarUrl(user?.avatar) || '',
    profilePhoto: getFullAvatarUrl(user?.profilePhoto) || ''
  });

  useEffect(() => {
    console.log('User data changed, updating form:', user);
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        dateOfBirth: user.dateOfBirth || null,
        instruments: user.instruments || [],
        preferredLanguage: user.preferredLanguage || 'en',
        avatar: getFullAvatarUrl(user.avatar) || '',
        profilePhoto: getFullAvatarUrl(user.profilePhoto) || ''
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchChurchData = async () => {
      if (user?.church) {
        try {
          const response = await get('churches/me');
          if (response?.data?.name) {
            setChurchName(response.data.name);
          }
        } catch (err) {
          console.error('Error fetching church data:', err);
        }
      }
    };

    fetchChurchData();
  }, [user, get]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(' Input changed:', { field: name, value });
    console.log('Current formData:', formData);
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      console.log('Updated formData:', newData);
      return newData;
    });
  };

  const handleDateChange = (date) => {
    console.log(' Date changed:', date);
    setFormData(prev => ({
      ...prev,
      dateOfBirth: date
    }));
  };

  const handleInstrumentChange = (e) => {
    setFormData(prev => ({
      ...prev,
      instruments: e.target.value
    }));
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('📸 [ProfileSettings] Starting avatar upload for file:', file.name);
      const formData = new FormData();
      formData.append('file', file);

      try {
        setLoading(true);
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        };
        console.log('📸 [ProfileSettings] Uploading file...');
        const response = await post('upload', formData, config);
        console.log('📸 [ProfileSettings] Upload response:', response);

        if (response?.url) {
          const avatarUrl = response.url;
          console.log('🖼️ [ProfileSettings] Received avatar URL:', avatarUrl);
          
          // Update user profile with new avatar
          console.log('👤 [ProfileSettings] Updating user profile with avatar...');
          const updateResponse = await put('users/me', { 
            profilePhoto: avatarUrl, // Use profilePhoto field as that's what the server expects
            avatar: avatarUrl // Also update avatar field for compatibility
          });
          console.log('👤 [ProfileSettings] Update response:', updateResponse);

          if (updateResponse?.data) {
            const fullAvatarUrl = getFullAvatarUrl(avatarUrl);
            console.log('🖼️ [ProfileSettings] Full avatar URL:', fullAvatarUrl);
            
            // Update the form data
            setFormData(prev => {
              console.log('📝 [ProfileSettings] Previous form data:', prev);
              const newData = {
                ...prev,
                avatar: fullAvatarUrl,
                profilePhoto: fullAvatarUrl
              };
              console.log('📝 [ProfileSettings] New form data:', newData);
              return newData;
            });

            // Update the user context with the new data
            console.log('👤 [ProfileSettings] Updating user context...');
            updateUser({
              ...updateResponse.data,
              avatar: avatarUrl, // Keep both fields for compatibility
              profilePhoto: avatarUrl
            });
            
            // Save to localStorage to ensure persistence across page refreshes
            localStorage.setItem('userProfilePhoto', avatarUrl);
            
            setSuccess(true);
            toast.success(t('settings.profile.avatarUpdateSuccess', 'Profile picture updated successfully'));
          }
        }
      } catch (err) {
        console.error('❌ [ProfileSettings] Upload error:', err);
        setError(t('settings.profile.avatarUploadError', 'Failed to upload avatar'));
        toast.error(t('settings.profile.avatarUploadError', 'Failed to upload avatar'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    console.log('Save button clicked');
    console.log('Current formData state:', formData);
    
    setLoading(true);
    setError('');
    setSuccess(false);

    const requestData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : null,
      instruments: formData.instruments,
      preferredLanguage: formData.preferredLanguage,
      avatar: formData.avatar,
      profilePhoto: formData.profilePhoto
    };

    console.log('Sending profile update with data:', requestData);

    try {
      console.log('Making API request to update profile');
      const response = await put('users/me', requestData);
      console.log('Profile update response:', response);

      if (response?.data) {
        // Update the user context with the new data
        updateUser({
          ...user,
          ...response.data
        });

        // Update form data
        setFormData(prev => ({
          ...prev,
          ...response.data
        }));

        setSuccess(true);
        toast.success(t('settings.profile.updateSuccess', 'Profile updated successfully'));
      } else {
        console.error('Invalid response format:', response);
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError(t('settings.profile.updateError', 'Failed to update profile'));
      toast.error(t('settings.profile.updateError', 'Failed to update profile'));
    } finally {
      setLoading(false);
    }
  };

  const instruments = [
    'Vocals',
    'Guitar',
    'Piano',
    'Drums',
    'Bass',
    'Violin',
    'Flute',
    'Saxophone',
    'Trumpet'
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'es', name: 'Spanish' }
  ];

  const isMobile = window.innerWidth < 600;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant="h6" gutterBottom>
        {t('settings.profile.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t('settings.profile.subtitle')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{t('settings.profile.success')}</Alert>}

      <form onSubmit={handleSave}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={formData.avatar}
                alt={`${formData.firstName} ${formData.lastName}`}
                sx={{
                  width: { xs: 80, sm: 120, md: 150 },
                  height: { xs: 80, sm: 120, md: 150 },
                  mb: { xs: 1, sm: 2 }
                }}
              />
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatar-upload"
                type="file"
                onChange={handleAvatarChange}
              />
              <label htmlFor="avatar-upload" style={{ width: '100%' }}>
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PhotoCamera />}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                >
                  {t('settings.profile.uploadAvatar')}
                </Button>
              </label>
            </Box>
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('settings.profile.name')}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('settings.profile.lastName')}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('settings.profile.email')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('settings.profile.phone')}
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t('settings.profile.dateOfBirth')}
                    value={formData.dateOfBirth}
                    onChange={handleDateChange}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        fullWidth 
                        size={isMobile ? "small" : "medium"}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="instruments-label">{t('settings.profile.instruments')}</InputLabel>
                  <Select
                    labelId="instruments-label"
                    multiple
                    value={formData.instruments}
                    onChange={handleInstrumentChange}
                    size={isMobile ? "small" : "medium"}
                    renderValue={(selected) => (
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 0.5,
                        maxWidth: '100%',
                        overflow: 'hidden'
                      }}>
                        {selected.map((value) => (
                          <Chip 
                            key={value} 
                            label={value}
                            size={isMobile ? "small" : "medium"}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {instruments.map((instrument) => (
                      <MenuItem key={instrument} value={instrument}>
                        {instrument}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Church Information */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: { xs: 1, sm: 2 },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    mt: { xs: 1, sm: 2 },
                    width: '100%'
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t('settings.profile.church')}
                  </Typography>
                  <Typography variant="body1">
                    {churchName || (user?.church ? t('settings.profile.loading') : t('settings.profile.notAssigned'))}
                  </Typography>
                </Box>
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: { xs: 1, sm: 2 },
                  mt: { xs: 2, sm: 3 },
                  width: '100%'
                }}>
                  <Button
                    variant="outlined"
                    onClick={() => window.location.reload()}
                  >
                    {t('settings.profile.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      t('settings.profile.saveChanges')
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default ProfileSettings;
